import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import libraryService from '../../services/libraryService';
import BarcodeScanner from '../BarcodeScanner';
import { MagnifyingGlass, Scan, User, BookOpen, Info } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import DialogModal from '../DialogModal';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cheque', label: 'Cheque' },
];

const SUB_TABS = [
  { value: 'issue', label: 'Issue / Return' },
  { value: 'loans', label: 'Loans' },
  { value: 'fines', label: 'Fines' },
  { value: 'copies', label: 'Copies' },
];

function CopiesPanel() {
  const [copies, setCopies] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState('');
  const [barcode, setBarcode] = useState('');
  const [accessionNumber, setAccessionNumber] = useState('');
  const [location, setLocation] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, onSubmit }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [copiesData, itemsData] = await Promise.all([
          libraryService.getCopies(),
          libraryService.getItems({ limit: 100, sort_by: 'newest' }),
        ]);
        if (!cancelled) {
          setCopies(copiesData || []);
          setItems(itemsData.items || []);
        }
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Unable to load copies');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  const addCopy = async (e) => {
    e.preventDefault();
    if (!itemId || !barcode.trim()) {
      toast.error('Select a title and enter a barcode');
      return;
    }
    try {
      await libraryService.createCopy({ item_id: itemId, barcode: barcode.trim(), accession_number: accessionNumber.trim() || null, location: location.trim() || null });
      toast.success('Copy added');
      setBarcode('');
      setAccessionNumber('');
      setLocation('');
      setRefreshKey((k) => k + 1);
    } catch (error) {
      toast.error(error.message || 'Unable to add copy');
    }
  };

  const itemTitle = (id) => items.find((it) => it.id === id)?.title || id;

  const retireCopy = (copy) => {
    setPromptDialog({
      title: 'Retire Copy',
      message: 'Reason for retiring this copy (weeding/deaccessioning):',
      onSubmit: async (value) => {
        const reason = value?.trim();
        if (!reason) return;
        try {
          await libraryService.retireCopy(copy.id, reason);
          toast.success('Copy retired');
          setRefreshKey((k) => k + 1);
        } catch (error) {
          toast.error(error.response?.data?.detail || error.message || 'Unable to retire this copy');
        }
      },
    });
  };

  return (
    <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
      <form onSubmit={addCopy} style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <select className="form-control" value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">Select title</option>
          {items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
        </select>
        <input className="form-control" placeholder="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        <input className="form-control" placeholder="Accession # (optional)" value={accessionNumber} onChange={(e) => setAccessionNumber(e.target.value)} />
        <input className="form-control" placeholder="Shelf location (optional)" value={location} onChange={(e) => setLocation(e.target.value)} />
        <button className="btn btn-primary" type="submit">Add Copy</button>
      </form>

      {loading ? (
        <div className="empty-state">Loading copies...</div>
      ) : copies.length === 0 ? (
        <div className="empty-state">No physical copies added yet.</div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          {copies.map((c) => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{itemTitle(c.item_id)}</div>
                <div style={{ color: '#6b7280' }}>{c.barcode}{c.location ? ` · ${c.location}` : ''}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="badge">{c.status}</span>
                {c.status !== 'checked_out' && c.status !== 'retired' && (
                  <button className="btn btn-secondary" type="button" style={{ padding: '2px 8px', fontSize: 12 }} onClick={() => retireCopy(c)}>Retire</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason', required: true }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

function IssuePanel() {
  const [borrowerQuery, setBorrowerQuery] = useState('');
  const [borrowerResults, setBorrowerResults] = useState([]);
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [scanningBorrower, setScanningBorrower] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanningBook, setScanningBook] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [patronCard, setPatronCard] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, onSubmit }
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => {
    if (borrowerQuery.trim().length < 2) {
      setBorrowerResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await libraryService.lookupBorrowers(borrowerQuery.trim());
        setBorrowerResults(results || []);
      } catch (error) {
        toast.error(error.message || 'Search failed');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [borrowerQuery]);

  const loadPatronCard = async (userId) => {
    try {
      setPatronCard(await libraryService.getPatronCard(userId));
    } catch (error) {
      setPatronCard(null);
    }
  };

  const selectBorrower = (borrower) => {
    setSelectedBorrower(borrower);
    setBorrowerResults([]);
    setBorrowerQuery('');
    setScanningBorrower(false);
    loadPatronCard(borrower.user_id);
  };

  const applyPatronStatus = async (suspending, reason) => {
    try {
      await libraryService.setPatronStatus(selectedBorrower.user_id, { status: suspending ? 'suspended' : 'active', reason: reason?.trim() || null });
      toast.success(suspending ? 'Patron suspended' : 'Patron reactivated');
      loadPatronCard(selectedBorrower.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unable to update patron status');
    }
  };

  const toggleSuspension = () => {
    if (!patronCard) return;
    const suspending = patronCard.status !== 'suspended';
    if (suspending) {
      setPromptDialog({
        title: 'Suspend Patron',
        message: 'Reason for suspending this patron:',
        onSubmit: (value) => {
          const reason = value?.trim();
          if (!reason) return;
          applyPatronStatus(true, reason);
        },
      });
      return;
    }
    applyPatronStatus(false, null);
  };

  const handleBorrowerScan = (decoded) => {
    setScanningBorrower(false);
    setBorrowerQuery(decoded);
  };

  const lookup = async (barcode) => {
    const code = (barcode || barcodeInput).trim();
    if (!code) return;
    setLookingUp(true);
    setLookupResult(null);
    try {
      const result = await libraryService.scanLookup(code);
      setLookupResult(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'No copy found with that barcode');
    } finally {
      setLookingUp(false);
    }
  };

  const returnFromLookup = async () => {
    if (!lookupResult?.active_loan) return;
    try {
      const result = await libraryService.returnLoan(lookupResult.active_loan.id);
      if (result.fine) {
        toast.success(`Returned. Overdue fine posted: GHS ${result.fine.amount.toFixed(2)}`);
      } else {
        toast.success('Returned');
      }
      lookup(barcodeInput);
    } catch (error) {
      toast.error(error.message || 'Unable to return');
    }
  };

  const issue = async (barcode) => {
    if (!selectedBorrower) {
      toast.error('Select a borrower first');
      return;
    }
    const code = (barcode || barcodeInput).trim();
    if (!code) return;
    setIssuing(true);
    try {
      await libraryService.issueLoan({ barcode: code, borrower_user_id: selectedBorrower.user_id });
      toast.success(`Issued to ${selectedBorrower.name}`);
      setBarcodeInput('');
      setScanningBook(false);
      setLookupResult(null);
    } catch (error) {
      toast.error(error.message || 'Unable to issue this copy');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, display: 'grid', gap: 16 }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <User size={16} /> 1. Borrower
        </div>
        {selectedBorrower ? (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{selectedBorrower.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{selectedBorrower.type} · {selectedBorrower.identifier}</div>
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => { setSelectedBorrower(null); setPatronCard(null); }}>Change</button>
            </div>
            {patronCard && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, borderTop: '1px solid #f3f4f6', paddingTop: 8 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ color: patronCard.status === 'suspended' ? '#dc2626' : '#16a34a', fontWeight: 700, textTransform: 'capitalize' }}>{patronCard.status}</span>
                  <span style={{ color: patronCard.at_limit ? '#dc2626' : '#6b7280' }}>{patronCard.active_loans}/{patronCard.loan_limit} loans</span>
                  {patronCard.outstanding_fines > 0 && <span style={{ color: '#dc2626' }}>GHS {patronCard.outstanding_fines.toFixed(2)} owed</span>}
                </div>
                <button className="btn btn-secondary" type="button" style={{ padding: '2px 8px', fontSize: 12 }} onClick={toggleSuspension}>
                  {patronCard.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <MagnifyingGlass size={16} style={{ position: 'absolute', left: 10, top: 11 }} />
                <input className="form-control" style={{ paddingLeft: 36 }} placeholder="Search name or ID" value={borrowerQuery} onChange={(e) => setBorrowerQuery(e.target.value)} />
              </div>
              <button className="btn btn-secondary" type="button" onClick={() => setScanningBorrower((v) => !v)}>
                <Scan size={16} /> {scanningBorrower ? 'Cancel Scan' : 'Scan ID'}
              </button>
            </div>
            {scanningBorrower && <BarcodeScanner active={scanningBorrower} onScan={handleBorrowerScan} manualLabel="Enter student/staff ID" />}
            {borrowerResults.length > 0 && (
              <div style={{ display: 'grid', gap: 6, maxHeight: 200, overflow: 'auto' }}>
                {borrowerResults.map((b) => (
                  <button key={b.user_id} type="button" onClick={() => selectBorrower(b)} className="btn btn-secondary" style={{ justifyContent: 'flex-start', textAlign: 'left' }}>
                    {b.name} <span style={{ color: '#6b7280', marginLeft: 6 }}>({b.type} · {b.identifier})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <BookOpen size={16} /> 2. Book copy
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-control" placeholder="Barcode" value={barcodeInput} onChange={(e) => { setBarcodeInput(e.target.value); setLookupResult(null); }} onKeyDown={(e) => e.key === 'Enter' && lookup()} />
          <button className="btn btn-secondary" type="button" onClick={() => setScanningBook((v) => !v)}>
            <Scan size={16} /> {scanningBook ? 'Cancel Scan' : 'Scan Barcode'}
          </button>
          <button className="btn btn-secondary" type="button" disabled={lookingUp} onClick={() => lookup()}>
            <Info size={16} /> {lookingUp ? 'Looking up...' : 'Look Up'}
          </button>
          <button className="btn btn-primary" type="button" disabled={issuing || !selectedBorrower} onClick={() => issue()}>
            {issuing ? 'Issuing...' : 'Issue'}
          </button>
        </div>
        {scanningBook && <BarcodeScanner active={scanningBook} onScan={(code) => { setBarcodeInput(code); lookup(code); }} manualLabel="Enter book barcode" />}

        {lookupResult && (
          <div style={{ marginTop: 10, border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{lookupResult.item_title || lookupResult.title || 'Book copy'}</div>
                <div style={{ color: '#6b7280' }}>{lookupResult.barcode}{lookupResult.location ? ` · ${lookupResult.location}` : ''}</div>
              </div>
              <span className="badge">{lookupResult.status}</span>
            </div>
            {lookupResult.active_loan ? (
              <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#dc2626' }}>
                  Checked out to {lookupResult.active_loan.borrower_name || lookupResult.active_loan.borrower_user_id}
                  {lookupResult.active_loan.due_date ? ` · due ${lookupResult.active_loan.due_date}` : ''}
                </div>
                <button className="btn btn-secondary" type="button" style={{ padding: '2px 8px', fontSize: 12 }} onClick={returnFromLookup}>Return</button>
              </div>
            ) : (
              <div style={{ marginTop: 8, borderTop: '1px solid #f3f4f6', paddingTop: 8, color: '#16a34a' }}>
                Available to issue
              </div>
            )}
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason', required: true }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

function LoansPanel() {
  const [statusFilter, setStatusFilter] = useState('active');
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lostPrompt, setLostPrompt] = useState(null);
  const [replacementCost, setReplacementCost] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = statusFilter === 'overdue' ? { overdue_only: true, status: 'active' } : (statusFilter === 'all' ? {} : { status: statusFilter });
        const data = await libraryService.getLoans({ ...params, limit: 50 });
        if (!cancelled) setLoans(data.items || []);
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Unable to load loans');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleReturn = async (loan) => {
    try {
      const result = await libraryService.returnLoan(loan.id);
      if (result.fine) {
        toast.success(`Returned. Overdue fine posted: GHS ${result.fine.amount.toFixed(2)}`);
      } else {
        toast.success('Returned');
      }
      refresh();
    } catch (error) {
      toast.error(error.message || 'Unable to return');
    }
  };

  const handleRenew = async (loan) => {
    try {
      await libraryService.renewLoan(loan.id);
      toast.success('Renewed');
      refresh();
    } catch (error) {
      toast.error(error.message || 'Unable to renew');
    }
  };

  const submitLost = async () => {
    const cost = parseFloat(replacementCost);
    if (!cost || cost <= 0) {
      toast.error('Enter a valid replacement cost');
      return;
    }
    try {
      const result = await libraryService.reportLoanLost(lostPrompt.id, cost);
      toast.success(`Marked lost. Fine posted: GHS ${result.fine.amount.toFixed(2)}`);
      setLostPrompt(null);
      setReplacementCost('');
      refresh();
    } catch (error) {
      toast.error(error.message || 'Unable to report lost');
    }
  };

  return (
    <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
      <select className="form-control" style={{ width: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        <option value="active">Active</option>
        <option value="overdue">Overdue</option>
        <option value="returned">Returned</option>
        <option value="lost">Lost</option>
        <option value="all">All</option>
      </select>

      {loading ? (
        <div className="empty-state">Loading loans...</div>
      ) : loans.length === 0 ? (
        <div className="empty-state">No loans found.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {loans.map((loan) => (
            <div key={loan.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{loan.item_title || 'Untitled'} <span style={{ color: '#6b7280', fontWeight: 400 }}>({loan.barcode})</span></div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{loan.borrower_name || 'Unknown borrower'} · Due {loan.due_date}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {loan.is_overdue && <span className="badge" style={{ background: '#fee2e2', color: '#dc2626' }}>Overdue</span>}
                  <span className="badge">{loan.status}</span>
                </div>
              </div>
              {loan.status === 'active' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="button" onClick={() => handleReturn(loan)}>Return</button>
                  <button className="btn btn-secondary" type="button" onClick={() => handleRenew(loan)}>Renew</button>
                  <button className="btn btn-danger" type="button" onClick={() => setLostPrompt(loan)}>Report Lost</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lostPrompt && (
        <div className="modal-overlay" onClick={() => setLostPrompt(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Report Lost</h2>
              <button className="modal-close" type="button" onClick={() => setLostPrompt(null)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
              <div>{lostPrompt.item_title}</div>
              <input className="form-control" type="number" placeholder="Replacement cost (GHS)" value={replacementCost} onChange={(e) => setReplacementCost(e.target.value)} />
              <div className="modal-actions">
                <button className="btn btn-secondary" type="button" onClick={() => setLostPrompt(null)}>Cancel</button>
                <button className="btn btn-primary" type="button" onClick={submitLost}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FineSettingsForm({ onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    libraryService.getFineSettings().then(setSettings).catch(() => toast.error('Unable to load fine settings'));
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await libraryService.updateFineSettings({
        fine_per_day: parseFloat(settings.fine_per_day),
        max_fine_days: parseInt(settings.max_fine_days, 10),
      });
      setSettings(updated);
      toast.success('Fine settings saved');
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.message || 'Unable to save fine settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 12 }}>
      <button type="button" className="btn btn-secondary" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide Fine Settings' : 'Fine Settings'}
      </button>
      {open && (
        settings ? (
          <form onSubmit={save} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>
              Rate (GHS/day)
              <input className="form-control" type="number" step="0.01" min="0" value={settings.fine_per_day}
                onChange={(e) => setSettings({ ...settings, fine_per_day: e.target.value })} style={{ width: 100, marginLeft: 6 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              Cap (days)
              <input className="form-control" type="number" step="1" min="0" value={settings.max_fine_days}
                onChange={(e) => setSettings({ ...settings, max_fine_days: e.target.value })} style={{ width: 80, marginLeft: 6 }} />
            </label>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </form>
        ) : (
          <div style={{ marginTop: 10, color: '#6b7280' }}>Loading...</div>
        )
      )}
    </div>
  );
}

function FinesSummaryBar({ refreshKey }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    libraryService.getFinesSummary().then((data) => { if (!cancelled) setSummary(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (!summary) return null;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div className="card" style={{ padding: 12, minWidth: 160 }}>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Outstanding</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>GHS {summary.outstanding_amount.toFixed(2)}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>{summary.outstanding_count} pending fine(s)</div>
        </div>
      </div>
      {summary.top_borrowers.length > 0 && (
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Top outstanding borrowers</div>
          <div style={{ display: 'grid', gap: 4 }}>
            {summary.top_borrowers.map((b, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span>{b.borrower_name || 'Unknown'}</span>
                <span>GHS {b.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FinesPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'school_admin';
  const [statusFilter, setStatusFilter] = useState('pending');
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waivePrompt, setWaivePrompt] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [paymentMethods, setPaymentMethods] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = statusFilter === 'all' ? {} : { status: statusFilter };
        const data = await libraryService.getFines({ ...params, limit: 50 });
        if (!cancelled) setFines(data.items || []);
      } catch (error) {
        if (!cancelled) toast.error(error.message || 'Unable to load fines');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handlePay = async (fine) => {
    try {
      await libraryService.payFine(fine.id, paymentMethods[fine.id] || 'cash');
      toast.success('Fine marked paid');
      refresh();
    } catch (error) {
      toast.error(error.message || 'Unable to record payment');
    }
  };

  const submitWaive = async () => {
    if (!waiveReason.trim()) {
      toast.error('A reason is required');
      return;
    }
    try {
      await libraryService.waiveFine(waivePrompt.id, waiveReason.trim());
      toast.success('Fine waived');
      setWaivePrompt(null);
      setWaiveReason('');
      refresh();
    } catch (error) {
      toast.error(error.message || 'Unable to waive fine');
    }
  };

  return (
    <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
      {isAdmin && <FinesSummaryBar refreshKey={refreshKey} />}
      {isAdmin && <FineSettingsForm onSaved={refresh} />}

      <select className="form-control" style={{ width: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        <option value="pending">Pending</option>
        <option value="paid">Paid</option>
        <option value="waived">Waived</option>
        <option value="all">All</option>
      </select>

      {loading ? (
        <div className="empty-state">Loading fines...</div>
      ) : fines.length === 0 ? (
        <div className="empty-state">No fines found.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {fines.map((fine) => (
            <div key={fine.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600 }}>GHS {fine.amount.toFixed(2)} — {fine.reason}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{fine.borrower_name || 'Unknown'} · {fine.item_title || ''}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span className="badge">{fine.status}</span>
                {fine.status === 'pending' && (
                  <>
                    <select className="form-control" style={{ width: 140 }} value={paymentMethods[fine.id] || 'cash'}
                      onChange={(e) => setPaymentMethods({ ...paymentMethods, [fine.id]: e.target.value })}>
                      {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <button className="btn btn-primary" type="button" onClick={() => handlePay(fine)}>Mark Paid</button>
                    <button className="btn btn-secondary" type="button" onClick={() => setWaivePrompt(fine)}>Waive</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {waivePrompt && (
        <div className="modal-overlay" onClick={() => setWaivePrompt(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Waive Fine</h2>
              <button className="modal-close" type="button" onClick={() => setWaivePrompt(null)}>×</button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 10 }}>
              <div>GHS {waivePrompt.amount.toFixed(2)} — {waivePrompt.reason}</div>
              <input className="form-control" placeholder="Reason for waiving" value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)} />
              <div className="modal-actions">
                <button className="btn btn-secondary" type="button" onClick={() => setWaivePrompt(null)}>Cancel</button>
                <button className="btn btn-primary" type="button" onClick={submitWaive}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CirculationPanel() {
  const [subTab, setSubTab] = useState('issue');

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {SUB_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={t.value === subTab ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setSubTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {subTab === 'issue' && <IssuePanel />}
      {subTab === 'loans' && <LoansPanel />}
      {subTab === 'fines' && <FinesPanel />}
      {subTab === 'copies' && <CopiesPanel />}
    </div>
  );
}

export default CirculationPanel;
